import { useState } from 'react'
import { Button, Tooltip } from '@mantine/core'
import { modals } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'
import type { Patient } from '@medplum/fhirtypes'
import { useClient } from '@/hooks/useClient'
import { useIsManager } from '@/hooks/useIsManager'
import { PhoneLink } from '@/components/ui/PhoneLink'
import { logError } from '@/utils/errorUtils'
import { formatDateTime } from '@/utils.ts'
import { formatPharmacyAddress, readDefaultPharmacy } from './defaultPharmacy'
import { PharmacySearchModal } from './PharmacySearchModal'

type Props = {
  patient: Patient
  refetchPatient: () => void
}

/**
 * The Default Pharmacy section on Demographics (PD-1386, ticket 04 variant A): an
 * inline section in the PCP house style. It renders purely from the Patient
 * extension's stored snapshot — a DoseSpot outage never blanks it — and its actions
 * write the chart only: the association is asserted by the next Rx Prescribe launch,
 * never by a card save, and a bare remove deliberately tells DoseSpot nothing (the
 * confirm copy states the consequence).
 */
export const DefaultPharmacySection = ({ patient, refetchPatient }: Props) => {
  const client = useClient()
  // Care coordinators may use the card — it's a demographics surface they can edit
  // elsewhere; only call-center managers get the disabled treatment.
  const { isCallCenterManager, loading: roleLoading } = useIsManager()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [removing, setRemoving] = useState(false)

  const stored = readDefaultPharmacy(patient)
  const actionsDisabled = isCallCenterManager || roleLoading

  const performRemove = async (storeName: string) => {
    if (!patient.id) return
    setRemoving(true)
    try {
      await client.doseSpotRemoveDefaultPharmacy({ patientId: patient.id })
      showNotification({ color: 'green', message: `${storeName} removed as the default pharmacy.` })
      refetchPatient()
    } catch (error) {
      logError('patient', 'Remove default pharmacy', error)
      showNotification({
        color: 'red',
        message: error instanceof Error ? error.message : 'Unable to remove the default pharmacy.',
      })
    } finally {
      setRemoving(false)
    }
  }

  const confirmRemove = () => {
    if (!stored) return
    modals.openConfirmModal({
      title: 'Remove default pharmacy?',
      children: (
        <p className="text-sm">
          New prescriptions in DoseSpot will no longer default to {stored.storeName}. If the patient
          was already linked to it in DoseSpot, it may stay selected there until it&apos;s changed
          during prescribing.
        </p>
      ),
      labels: { confirm: 'Remove', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        void performRemove(stored.storeName)
      },
    })
  }

  return (
    <>
      <div className="mt-6 flex items-center gap-3">
        <p className="text-2xl text-base">Default Pharmacy</p>
        <Tooltip
          // Never a silently disabled button (the PD-1318 manager treatment): the
          // role lookup is brief but it does disable the controls, so it says so.
          label={
            roleLoading
              ? 'Checking your permissions…'
              : 'Patient demographics edit permissions required'
          }
          disabled={!actionsDisabled}
        >
          {/* The span is the hover target: a disabled control fires no mouse events. */}
          <span className="inline-flex items-center gap-3">
            <Button
              variant="outline"
              size="xs"
              disabled={actionsDisabled}
              onClick={() => setIsModalOpen(true)}
              className="!text-primary !border-[#D0E1F2] !bg-gradient-to-b from-[#F0F7FF] to-[#FFFFFF] hover:from-[#FFFFFF]"
            >
              {stored ? 'Change' : 'Add'}
            </Button>
            {stored && (
              <Button
                variant="outline"
                color="red"
                size="xs"
                disabled={actionsDisabled}
                loading={removing}
                onClick={confirmRemove}
              >
                Remove
              </Button>
            )}
          </span>
        </Tooltip>
      </div>

      {stored ? (
        <table className="mt-2 text-sm">
          <tbody>
            <tr className="h-[34px]">
              <td width={80} className="text-sm font-bold text-[#7D7987]">
                Name:
              </td>
              <td>{stored.storeName}</td>
            </tr>
            <tr className="h-[34px]">
              <td width={80} className="text-sm font-bold text-[#7D7987]">
                Address:
              </td>
              <td>{formatPharmacyAddress(stored)}</td>
            </tr>
            {stored.phone && (
              <tr className="h-[34px]">
                <td width={80} className="text-sm font-bold text-[#7D7987]">
                  Phone:
                </td>
                <td>
                  <PhoneLink phone={stored.phone} />
                </td>
              </tr>
            )}
            {/* Absent fax/type rows are omitted, not dashed — "when available". */}
            {stored.fax && (
              <tr className="h-[34px]">
                <td width={80} className="text-sm font-bold text-[#7D7987]">
                  Fax:
                </td>
                <td>
                  <PhoneLink phone={stored.fax} />
                </td>
              </tr>
            )}
            {stored.specialties && (
              <tr className="h-[34px]">
                <td width={80} className="text-sm font-bold text-[#7D7987]">
                  Type:
                </td>
                <td>{stored.specialties}</td>
              </tr>
            )}
            {/* Absent on defaults stored before middleware stamped writes (PD-1504). */}
            {stored.updatedAt && (
              <tr className="h-[34px]">
                <td width={120} className="text-sm font-bold text-[#7D7987]">
                  Last updated:
                </td>
                <td>{formatDateTime(stored.updatedAt)}</td>
              </tr>
            )}
          </tbody>
        </table>
      ) : (
        <p className="mt-2 text-sm text-gray-400">No default pharmacy added.</p>
      )}

      {isModalOpen && patient.id && (
        <PharmacySearchModal
          patientId={patient.id}
          current={stored}
          onClose={() => setIsModalOpen(false)}
          onSaved={() => {
            setIsModalOpen(false)
            refetchPatient()
          }}
        />
      )}
    </>
  )
}
